import { FC } from 'react';
import TickIcon from '@/assets/icons/tick.svg?react';
import DotIcon from '@/assets/icons/dot.svg?react';
import clsx from 'clsx';

interface StyledStepperProps {
  steps: string[];
  activeStep: number;
  setStep: (step: number) => void;
  disableNext?: boolean;
  disableConditions?: Record<number, boolean>;
}

interface StepIconProps {
  active: boolean;
  completed: boolean;
}

const StepIcon: FC<StepIconProps> = ({ active, completed }) => {
  return (
    <>
      {active ? (
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-600 ring-4 ring-gray-50 z-1">
          <DotIcon className="size-2 fill-white" />
        </span>
      ) : completed ? (
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-600 ring-4 ring-gray-50 z-1">
          <TickIcon className="size-4 fill-white" />
        </span>
      ) : (
        <span className="flex size-8 items-center justify-center rounded-full bg-white border border-gray-300 ring-4 ring-gray-50 z-1">
          <DotIcon className="fill-gray-300 size-2" />
        </span>
      )}
    </>
  );
};

const StyledStepper: FC<StyledStepperProps> = ({
  steps,
  activeStep,
  setStep,
  disableNext = false,
  disableConditions = {},
}) => {
  return (
    <ol className="flex items-center justify-center relative w-full mx-auto mb-4">
      {steps.map((step, index) => (
        <li
          key={`step-${index}`}
          className={clsx(
            'flex flex-col items-center justify-center gap-3 relative w-32 md:w-48 lg:w-60 xl:w-80',
            {
              'hover:cursor-pointer':
                index <= activeStep ||
                !disableNext ||
                disableConditions[index] === false,
            },
          )}
          onClick={() => {
            if (
              index > activeStep &&
              (disableNext || disableConditions[index] === true)
            )
              return;
            setStep(index);
          }}
        >
          <StepIcon
            active={index === activeStep}
            completed={index < activeStep}
          />
          {index < steps.length - 1 && (
            <span
              className={clsx('absolute left-1/2 top-4 w-full h-0.5', {
                'bg-brand-600': index < activeStep,
                'bg-[#E2E8F0]': index >= activeStep,
              })}
            />
          )}
          <h2
            className={clsx('text-sm leading-6 font-medium text-center', {
              'text-brand-700': index <= activeStep,
              'text-gray-900': index > activeStep,
            })}
          >
            {step}
          </h2>
        </li>
      ))}
    </ol>
  );
};

export default StyledStepper;
